export const internationalToPolish: { [key: string]: string } = {
    'B': 'H',
    'A#': 'B',
    'C#': 'Cis',
    'D#': 'Dis',
    'F#': 'Fis',
    'G#': 'Gis'
};

const chromaticScale: string[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const polishToInternationalMapping: { [key: string]: string } = {
    'H': 'B',
    'h': 'b',
    'Cis': 'C#',
    'cis': 'c#',
    'Dis': 'D#',
    'dis': 'd#',
    'Fis': 'F#',
    'fis': 'f#',
    'Gis': 'G#',
    'gis': 'g#',
    'Ais': 'A#',
    'ais': 'a#'
};

const flatToSharp: { [key: string]: string } = {
    'Db': 'C#',
    'Eb': 'D#',
    'Gb': 'F#',
    'Ab': 'G#',
    'Bb': 'A#',
    'db': 'c#',
    'eb': 'd#',
    'gb': 'f#',
    'ab': 'g#',
    'bb': 'a#'
};

export interface ChordInfo {
    baseNote: string;
    chordType: 'major' | 'minor';
    modifier: string;
    system: string;
    input: string;
    isSongInPolish?: boolean;
    basNotePolish?: string;
}

interface KeyAnalysis {
    key: string;
    inKey: string[];
    notInKey: string[];
}

export function calculateKeyOfSong(chordInfos: ChordInfo[]): KeyAnalysis[] {
    const chordsInKey: { [key: string]: string[] } = {};

    for (let i = 0; i < chromaticScale.length; i++) {
        const s = chromaticScale.slice(i).concat(chromaticScale.slice(0, i));
        chordsInKey[s[0]] = [
            s[0],        // I Major
            s[2] + 'm',  // ii Minor
            s[4] + 'm',  // iii Minor
            s[5],        // IV Major
            s[7],        // V Dominant
            s[9] + 'm',  // vi Minor
            s[11] + 'dim'// vii° Diminished
        ];
    }

    const uniqueChords = [...new Set(chordInfos.map(info =>
        info.baseNote + (info.chordType === 'minor' ? 'm' : '')))].sort();

    const chordKeys = Object.keys(chordsInKey)
        .map(key => ({
            key,
            inKey: uniqueChords.filter(chord => chordsInKey[key].includes(chord)),
            notInKey: uniqueChords.filter(chord => !chordsInKey[key].includes(chord))
        }))
        .sort((a, b) => b.inKey.length - a.inKey.length);

    return chordKeys.filter(key => key.inKey.length === chordKeys[0].inKey.length);
}

export function transposeChord(info: ChordInfo, semitones: number): string {
    let index = chromaticScale.indexOf(info.baseNote);
    const direction = semitones > 0 ? 1 : -1;
    const offset = Math.abs(semitones);

    for (let i = 0; i < offset; i++) {
        index = (index + direction + chromaticScale.length) % chromaticScale.length;
    }

    if (index === -1) {
        console.error(`Could not find index for chord: ${info.baseNote} (${index})`);
        return info.input;
    }

    const note = chromaticScale[index];
    const polished = info.isSongInPolish ? (internationalToPolish[note] || note) : note;

    if(!polished) {
        console.error(`Could not find polished note for: ${note}`);
        return info.input;
    }

    return (info.chordType === 'minor' ? polished?.toLowerCase() : polished) + (info.modifier || '');
}

export function calculateChordDistance(chord1: string, chord2: string, isPolish: boolean): number {
    const info1 = extractChordInfo(chord1, isPolish);
    const info2 = extractChordInfo(chord2, isPolish);
    const index1 = chromaticScale.indexOf(info1.baseNote.toUpperCase());
    const index2 = chromaticScale.indexOf(info2.baseNote.toUpperCase());

    if (index1 === -1 || index2 === -1) {
        console.error(`Could not find indices for chords: ${chord1} (${index1}), ${chord2} (${index2})`);
        return 0;
    }

    let diff = index2 - index1;
    if (diff > 6) {
        diff -= 12;
    } else if (diff <= -6) {
        diff += 12;
    }
    return diff;
}

export function calculateNextKey(chord: string, shift: number): string {
    const index = chromaticScale.indexOf(extractChordInfo(chord).baseNote.toUpperCase());
    const newIndex = (index + shift + chromaticScale.length) % chromaticScale.length;
    return chromaticScale[newIndex];
}

export function extractChordInfo(chord: string, isPolish: boolean = false): ChordInfo {
    const regex = /^([A-Ga-gHh](b|#|is)?)(m)?(.*)?/;
    const match = chord.match(regex);

    if (!match) {
        console.error(`[ERROR]Chord "${chord}" does not match regex ${regex}`);
        return {
            baseNote: 'C',
            chordType: 'major',
            modifier: '',
            system: 'polish',
            input: chord
        };
    }

    let baseNote = match[1];
    let chordType = match[3];
    let modifier = match[4] || '';
    let system = '?';

    if (baseNote.includes('#')) system = 'international';

    if (isPolish) {
        if (baseNote === 'B' || baseNote === 'b') {
            baseNote = baseNote === 'B' ? 'A#' : 'a#';
        }
        system = 'polish';
    }

    if (baseNote in polishToInternationalMapping) {
        system = 'polish';
        baseNote = polishToInternationalMapping[baseNote];
    }

    if (baseNote in flatToSharp) {
        system = 'international';
        baseNote = flatToSharp[baseNote];
    }

    try {
        const match2 = (chordType + modifier).match(/(min|maj).*/);
        if (match2) {
            if (match2[1] === 'min') {
                chordType = 'minor';
                modifier = 'm' + modifier;
                modifier = modifier.replace('min', 'm');
            } else {
                chordType = 'major';
                modifier = modifier.replace('maj', 'M');
            }
        }
    } catch (e) {
        console.log('error parsing chord extension', e);
    }

    let chordTypeRes : 'major' | 'minor';

    if (chordType === 'm' || /^[a-z].*/.test(baseNote)) {
        chordTypeRes = 'minor';
    } else {
        chordTypeRes = 'major';
    }

    baseNote = baseNote.toUpperCase();

    return {
        baseNote,
        chordType: chordTypeRes,
        modifier,
        system,
        input: chord,
        isSongInPolish: isPolish,
        basNotePolish: internationalToPolish[baseNote] || baseNote
    };
}
